/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DxpServiceId } from '@squiz/dxp-auth';
import { NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_SUCCESS,
  Button,
  DialogContentProps,
  FORM_THEME_LITE,
  Form,
  TOAST_TYPE_SUCCESS,
  TextField,
  TextFieldProps,
  Toast,
  ToastProps,
} from '@squiz/sds';
import {
  TenantSettingsServicePutSettingDto,
  getTenantSettingsService,
} from 'apps/console-core/src/Services/tenant-settings';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { DAM_INTEGRATIONS_DATA_IDS, DAM_INTEGRATIONS_FORM_IDS } from '../../DamIntegrations.constants';
import { SelectDamType } from '../../elements/SelectDamType/SelectDamType';
import { SelectDamTypeProps } from '../../elements/SelectDamType/SelectDamType.props';
import { AddDamIntegrationProps } from './AddDamIntegration.props';

/**
 * The form for adding a new DAM integration.
 */
export const AddDamIntegration = (dialogProps: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();

  const { close, setAfterClose, setButtonKeys, setButtons } = dialogProps;

  const addProps = dialogProps.customProps as AddDamIntegrationProps;
  const existingSetting = addProps.tenantSetting;

  // Tenant settings service
  const tenantSettingsService = getTenantSettingsService();

  // Toast
  const { updateActiveToast } = useToastStore();

  // Handle navigation
  const navigate = useNavigate();
  const goToIntegrationDetails = useCallback(
    (uuid: string): void => {
      const integrationDetailsPath = getCapabilityPath(`/dam-integrations/${uuid}`);
      navigate(integrationDetailsPath);
    },
    [navigate],
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  // Form refs
  const nameRef = useRef<HTMLInputElement>(null);
  const typeRef = useRef<HTMLInputElement>(null);

  // Validators
  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  // Labels
  const labels = useMemo(() => {
    return {
      add: t(CONFIGURATION_STRINGS.add),
      addDam: t(CONFIGURATION_STRINGS.addDam),
      cancel: t(CONFIGURATION_STRINGS.cancel),
      errorUnknown: t(CONFIGURATION_STRINGS.errorUnknown),
      friendlyName: t(CONFIGURATION_STRINGS.friendlyName),
      requiredField: t(CONFIGURATION_STRINGS.requiredField),
      successToastDescription: t(CONFIGURATION_STRINGS.damIntegrationAddedDescription),
      successToastTitle: t(CONFIGURATION_STRINGS.damIntegrationAdded),
    };
  }, [t]);

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>): void => {
      const nextErrors = { ...fieldErrors };
      inputs.forEach((input: HTMLInputElement): void => {
        const fieldId = input.getAttribute(`id`) as string;
        delete nextErrors[fieldId];

        if (!notEmptyValidator.validate(input.value)) {
          nextErrors[fieldId] = labels.requiredField;
        }
      });
      setFieldErrors(nextErrors);
    },
    [fieldErrors, labels, notEmptyValidator, setFieldErrors],
  );

  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );

  const clearSelectError = (fieldId: string): void => {
    const nextErrors = { ...fieldErrors };
    delete nextErrors[fieldId];
    setFieldErrors(nextErrors);
  };

  const submitHandler = useCallback(() => {
    setIsBusy(true);
    setFormError('');

    // Set up inputs
    const inputs = {
      name: nameRef.current as HTMLInputElement,
      type: typeRef.current as HTMLInputElement,
    };

    // Collect the current values of the form
    const name = inputs.name.value;
    const type = inputs.type.value;

    if (!notEmptyValidator.validate(name) || !notEmptyValidator.validate(type)) {
      const inputsToCheck = [inputs.name, inputs.type];
      validateFields(inputsToCheck);
      setSubmit(false);
      setIsBusy(false);
      return;
    }

    const integrationId = window.crypto.randomUUID();
    const existingIntegrations = existingSetting?.value.integrations ?? [];

    const value = {
      integrations: [
        ...existingIntegrations,
        {
          enabled: false,
          name,
          type,
          uuid: integrationId,
        },
      ],
    };

    const dto: TenantSettingsServicePutSettingDto = {
      schemaId: 'dam-integrations-public',
      serviceId: DxpServiceId.dxpDAMIntegrations,
      settingKey: existingSetting?.settingKey ?? window.crypto.randomUUID(),
      value,
    };

    const successToastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      title: labels.successToastTitle,
      toastContent: (
        <span>
          {labels.successToastDescription} <em>{name}</em>
        </span>
      ),
      toastType: TOAST_TYPE_SUCCESS,
    };

    lastValueFrom(tenantSettingsService.putSetting(dto))
      .then(() => {
        updateActiveToast(<Toast {...successToastProps} data-id={DAM_INTEGRATIONS_DATA_IDS.successToast} />);
        goToIntegrationDetails(integrationId);

        return true;
      })
      .catch(() => {
        setIsBusy(false);
        setSubmit(false);
        setFormError(labels.errorUnknown);
      });
  }, [
    existingSetting,
    goToIntegrationDetails,
    labels,
    notEmptyValidator,
    setIsBusy,
    setSubmit,
    tenantSettingsService,
    updateActiveToast,
    validateFields,
  ]);

  useEffect(() => {
    const cancelButton = (
      <Button
        data-id={DAM_INTEGRATIONS_DATA_IDS.actionAddIntegrationCancel}
        disabled={isBusy}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );
    const addButton = (
      <Button
        data-id={DAM_INTEGRATIONS_DATA_IDS.actionAddIntegrationConfirm}
        disabled={isBusy}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={labels.add}
        theme={BUTTON_THEME_SUCCESS}
      />
    );

    // Form submission check
    if (isFormSubmitted) {
      setButtonKeys([`add`]);
      setButtons([addButton]);

      if (!isBusy) {
        submitHandler();
      }
    } else {
      setButtonKeys([`cancel`, `add`]);
      setButtons([cancelButton, addButton]);
    }
  }, [close, isBusy, isFormSubmitted, labels, setAfterClose, setButtonKeys, setButtons, setSubmit, submitHandler]);

  // Name
  const nameFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[DAM_INTEGRATIONS_FORM_IDS.name] || null,
    fieldLabel: labels.friendlyName,
    id: DAM_INTEGRATIONS_FORM_IDS.name,
    name: DAM_INTEGRATIONS_FORM_IDS.name,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
    required: true,
    value: '',
  };

  // Type
  const typeFieldProps: SelectDamTypeProps = {
    callback: (): void => {
      clearSelectError(DAM_INTEGRATIONS_FORM_IDS.type);
    },
    errorMessage: fieldErrors[DAM_INTEGRATIONS_FORM_IDS.type] || null,
  };

  return (
    <div className="add-dam-integration__form">
      <Form errorMessage={formError} legend={labels.addDam} theme={FORM_THEME_LITE}>
        <TextField {...nameFieldProps} data-id={DAM_INTEGRATIONS_DATA_IDS.name} ref={nameRef} />
        <SelectDamType {...typeFieldProps} data-id={DAM_INTEGRATIONS_DATA_IDS.type} ref={typeRef} />
      </Form>
    </div>
  );
};
